/**
 * Surface-operator cutter mesh dispatcher.
 *
 * The three surface operators (box, cylinder, poly) all carve their host mesh
 * via a boolean modifier driven by a cutter `SceneObject`. This module is the
 * single place that builds that cutter mesh, so confirm-time creation and
 * persistent surface generators stay bit-for-bit aligned.
 *
 * The `Hollow` toggle replaces the solid prism with a thick-walled shell so
 * the boolean carves a frame-shaped void instead of a solid cavity. Wall
 * thickness is in world units; the slider scales by the host's bounding-box
 * diagonal via the existing `scaledByBounds` convention.
 *
 * The hollow path samples an outer 2D loop (rectangle / ellipse / user
 * polyline), then insets it via `insetPolygon2D`. Box and cylinder use the
 * matched outer/inner loops to emit all-quad shell topology; arbitrary poly
 * outlines continue through `buildExtrudedPolygon` so concave outlines and
 * holes remain triangulated by `THREE.ShapeUtils.triangulateShape`.
 */

import * as THREE from "three";
import { buildExtrudedPolygon } from "./generators/generatorGeom.ts";
import { recomputeLoopNormals } from "./meshOps.ts";
import type { PrimitiveGeometry } from "./primitives.ts";
import { generateCube, generateCylinder } from "./primitives.ts";
import {
  normalizeSurfaceProfile,
  type SurfaceCutterProfile,
  type SurfaceVec2,
  sampleSurfaceEllipseLoop,
  sampleSurfaceRectangleLoop,
  surfaceProfileRampZ,
} from "./surfaceProfiles.ts";
import {
  buildEdgesAndCornerEdges,
  triangulateTopologyForRendering,
} from "./topology.ts";
import type { SceneObject } from "./types.ts";

// ---------------------------------------------------------------------------
// Spec — captured at confirm time, persisted on the surface generator
// ---------------------------------------------------------------------------

/**
 * Per-kind silhouette + cut depth needed to (re)build a surface cutter mesh
 * at any later parameter change. Surface generators persist this source data
 * so `hollow` / `hollowThickness` can rebuild fresh geometry across reloads;
 * hollow changes topology, not just transformed positions.
 *
 * `cutStartOffset` is the inset offset already chosen by `surfaceCutStartOffset`
 * at confirm time and must be preserved across rebuilds so the visible cap
 * stays consistent with the original cut.
 */
export type SurfaceCutterSpec =
  | {
      kind: "box";
      width: number;
      depth: number;
      height: number;
      reverse: boolean;
      cutStartOffset: number;
      profile?: SurfaceCutterProfile;
    }
  | {
      kind: "cylinder";
      radiusX: number;
      radiusY: number;
      height: number;
      segments: number;
      reverse: boolean;
      cutStartOffset: number;
      profile?: SurfaceCutterProfile;
    }
  | {
      kind: "poly";
      outerLoop: ReadonlyArray<readonly [number, number]>;
      height: number;
      reverse: boolean;
      cutStartOffset: number;
      profile?: SurfaceCutterProfile;
    };

export interface SurfaceCutterOptions {
  hollow: boolean;
  hollowThickness: number;
  capScale?: number;
  loopCuts?: number;
  loopProfile?: number;
}

export interface SurfaceCutterMesh {
  geometry: PrimitiveGeometry;
  /** SceneObject scale to apply alongside the geometry. */
  scale: [number, number, number];
  /** Representative Z value of the cut-depth end cap in object-local space. */
  endCapZ: number;
  /**
   * Generator ref for the new SceneObject. Solid-mode primitives keep their
   * editable parametric generator (cube/cylinder); hollow-mode shells drop
   * it because the topology no longer matches a stock primitive.
   */
  generator: SceneObject["generator"] | undefined;
  /**
   * True when hollow was requested but the inset failed (concave self-
   * intersection or thickness too large). Geometry is solid in that case.
   * Caller may surface a one-time warning.
   */
  hollowFallback: boolean;
}

export function surfaceCapScaleFromParams(
  params: Readonly<Record<string, unknown>>,
): number {
  const value = Number(params.capScale ?? 1);
  return Number.isFinite(value) ? Math.max(0, value) : 1;
}

// ---------------------------------------------------------------------------
// 2D polygon utilities — shared by box, cylinder, poly hollow builders
// ---------------------------------------------------------------------------

type Vec2 = SurfaceVec2;

const EPSILON_AREA = 1e-12;

function signedArea(loop: ReadonlyArray<Vec2>): number {
  let acc = 0;
  for (let i = 0; i < loop.length; i++) {
    const [x1, y1] = loop[i];
    const [x2, y2] = loop[(i + 1) % loop.length];
    acc += x1 * y2 - x2 * y1;
  }
  return acc * 0.5;
}

function ensureCCW(loop: ReadonlyArray<Vec2>): {
  loop: ReadonlyArray<Vec2>;
  reversed: boolean;
} {
  if (signedArea(loop) >= 0) return { loop, reversed: false };
  return { loop: [...loop].reverse(), reversed: true };
}

/**
 * Returns true when any non-adjacent pair of edges in the closed loop crosses.
 * O(n²) — fine for cutter footprints which are tens of vertices at most.
 */
function loopSelfIntersects(loop: ReadonlyArray<Vec2>): boolean {
  const n = loop.length;
  if (n < 4) return false;
  for (let i = 0; i < n; i++) {
    const a = loop[i];
    const b = loop[(i + 1) % n];
    for (let j = i + 1; j < n; j++) {
      // Skip adjacent edges (they share a vertex by construction).
      if (j === i) continue;
      if ((j + 1) % n === i) continue;
      if ((i + 1) % n === j) continue;
      const c = loop[j];
      const d = loop[(j + 1) % n];
      if (segmentsCross(a, b, c, d)) return true;
    }
  }
  return false;
}

function segmentsCross(p1: Vec2, p2: Vec2, p3: Vec2, p4: Vec2): boolean {
  const d1 = sideOf(p3, p4, p1);
  const d2 = sideOf(p3, p4, p2);
  const d3 = sideOf(p1, p2, p3);
  const d4 = sideOf(p1, p2, p4);
  return (
    ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
    ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))
  );
}

function sideOf(a: Vec2, b: Vec2, p: Vec2): number {
  return (b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]);
}

/**
 * Inset a closed polygon inward by `thickness` using edge-bisector offset.
 *
 * Each vertex moves along the angle bisector of its incident edges by the
 * distance required to keep both adjacent edges at perpendicular distance
 * `thickness` from the new edges. Closed-form:
 *
 *     displacement = (n1 + n2) * thickness / (1 + n1·n2)
 *
 * where n1, n2 are the inward unit normals of the two edges meeting at the
 * vertex. This is exact for convex polygons and produces a valid inset for
 * many concave polygons too. For tricky concave cases the resulting loop may
 * self-intersect — the caller should validate via `loopSelfIntersects`.
 *
 * Returns the inset loop in the SAME winding as the input (re-reversed if we
 * had to flip it to CCW for the math).
 */
export function insetPolygon2D(
  loop: ReadonlyArray<Vec2>,
  thickness: number,
): { loop: Vec2[]; valid: boolean } {
  const n = loop.length;
  if (n < 3) return { loop: [], valid: false };
  if (!Number.isFinite(thickness) || thickness <= 0) {
    return { loop: loop.map((p) => [p[0], p[1]]), valid: thickness === 0 };
  }
  if (Math.abs(signedArea(loop)) < EPSILON_AREA) {
    return { loop: [], valid: false };
  }

  const { loop: ccw, reversed } = ensureCCW(loop);

  const inward: Vec2[] = []; // inward unit normal per edge (edge i: ccw[i] → ccw[i+1])
  for (let i = 0; i < n; i++) {
    const [x1, y1] = ccw[i];
    const [x2, y2] = ccw[(i + 1) % n];
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.hypot(dx, dy);
    if (len < 1e-12) return { loop: [], valid: false };
    // For CCW polygon, inward normal is 90° CCW rotation of edge direction.
    inward.push([-dy / len, dx / len]);
  }

  const result: Vec2[] = [];
  for (let i = 0; i < n; i++) {
    const n1 = inward[(i + n - 1) % n];
    const n2 = inward[i];
    const dot = n1[0] * n2[0] + n1[1] * n2[1];
    // Sharp acute corner near 180° reflex → bisector explodes; clamp to keep
    // the offset finite. The validation pass below catches the resulting
    // self-intersection if the geometry truly cannot be inset.
    const denom = Math.max(1e-3, 1 + dot);
    const k = thickness / denom;
    const dx = (n1[0] + n2[0]) * k;
    const dy = (n1[1] + n2[1]) * k;
    result.push([ccw[i][0] + dx, ccw[i][1] + dy]);
  }

  if (loopSelfIntersects(result)) return { loop: [], valid: false };

  // Verify the inset loop preserved orientation and didn't collapse area.
  const insetArea = signedArea(result);
  if (insetArea <= EPSILON_AREA) return { loop: [], valid: false };

  return { loop: reversed ? result.reverse() : result, valid: true };
}

// ---------------------------------------------------------------------------
// Thickness clamping — keep inner loop strictly interior
// ---------------------------------------------------------------------------

export function clampSurfaceHollowThickness(
  thickness: number,
  minFootprintExtent: number,
): number {
  // Half the minimum footprint extent is the geometric ceiling; pull back a
  // bit further so the inner loop doesn't degenerate even with rounding.
  const ceiling = Math.max(0, minFootprintExtent * 0.5 - 1e-4);
  if (!Number.isFinite(thickness) || thickness <= 0) return 0;
  return Math.min(thickness, ceiling);
}

// ---------------------------------------------------------------------------
// Hollow prism builders
// ---------------------------------------------------------------------------

interface HollowPrismResult {
  geometry: PrimitiveGeometry;
  endCapZ: number;
}

type ZAnchor = "centered" | "anchored";
type CapTopology = "quadBands" | "triangulated";

/**
 * Build a hollow extruded prism with an outer loop and an inset inner loop.
 * Returns `null` when the inset fails (concave self-intersection or over-
 * thick); caller falls back to solid in that case.
 *
 * `anchor` selects the local-z layout to match each cutter kind's existing
 * solid-mode convention so position/rotation on the SceneObject stay valid:
 *   - `"centered"` (box, cylinder): vertices span [-T/2, +T/2]; cap face
 *     sits at ±T/2 — same axis range as the unit cube/cylinder.
 *   - `"anchored"` (poly): vertices span [0, T] for forward cuts, or
 *     [-height, +cutStartOffset] for reverse cuts after a single down-shift.
 *     Cap face is at the cut-depth end (deepest into the host).
 */
function buildHollowPrism(
  outerLoop: ReadonlyArray<Vec2>,
  thickness: number,
  height: number,
  cutStartOffset: number,
  anchor: ZAnchor,
  reverse: boolean,
  capTopology: CapTopology,
): HollowPrismResult | null {
  const inset = insetPolygon2D(outerLoop, thickness);
  if (!inset.valid || inset.loop.length < 3) return null;

  const totalDepth = height + cutStartOffset;
  const outline: ReadonlyArray<Vec2> = outerLoop.map((p) => [p[0], p[1]]);
  const geo =
    capTopology === "quadBands"
      ? buildMatchedLoopHollowPrism(outline, inset.loop, totalDepth)
      : buildExtrudedPolygon(outline, totalDepth, [inset.loop]);
  if (!geo) return null;

  // buildExtrudedPolygon emits z ∈ [0, totalDepth]. Shift to match the
  // solid-mode convention for this kind so position math stays unchanged.
  let zShift = 0;
  let endCapZ: number;
  if (anchor === "centered") {
    zShift = -totalDepth / 2;
    endCapZ = reverse ? -totalDepth / 2 : totalDepth / 2;
  } else {
    // Anchored: forward → span [0, totalDepth] (no shift), cap at +height.
    // Reverse → shift down by `height` to span [-height, +cutStartOffset],
    // cap at -height (the cut-depth end).
    zShift = reverse ? -height : 0;
    endCapZ = reverse ? -height : height;
  }
  if (zShift !== 0) {
    for (let i = 2; i < geo.vertices.length; i += 3) geo.vertices[i] += zShift;
  }

  return { geometry: geo, endCapZ };
}

function buildMatchedLoopHollowPrism(
  outerLoop: ReadonlyArray<Vec2>,
  innerLoop: ReadonlyArray<Vec2>,
  depth: number,
): PrimitiveGeometry | null {
  const n = outerLoop.length;
  if (n < 3 || innerLoop.length !== n) return null;

  const topOuter = (i: number) => i % n;
  const topInner = (i: number) => n + (i % n);
  const botOuter = (i: number) => 2 * n + (i % n);
  const botInner = (i: number) => 3 * n + (i % n);

  const zTop = Math.max(1e-4, depth);
  const verts = new Float32Array(4 * n * 3);
  for (let i = 0; i < n; i++) {
    const [ox, oy] = outerLoop[i];
    const [ix, iy] = innerLoop[i];

    verts[topOuter(i) * 3] = ox;
    verts[topOuter(i) * 3 + 1] = oy;
    verts[topOuter(i) * 3 + 2] = zTop;

    verts[topInner(i) * 3] = ix;
    verts[topInner(i) * 3 + 1] = iy;
    verts[topInner(i) * 3 + 2] = zTop;

    verts[botOuter(i) * 3] = ox;
    verts[botOuter(i) * 3 + 1] = oy;
    verts[botOuter(i) * 3 + 2] = 0;

    verts[botInner(i) * 3] = ix;
    verts[botInner(i) * 3 + 1] = iy;
    verts[botInner(i) * 3 + 2] = 0;
  }

  const faceCount = 4 * n;
  const cornerCount = 4 * faceCount;
  const offsets = new Uint32Array(faceCount + 1);
  const corners = new Uint32Array(cornerCount);
  let ci = 0;
  let fi = 0;

  for (let i = 0; i < n; i++) {
    const i1 = (i + 1) % n;

    // Top cap ring, wound CCW from +Z.
    offsets[fi++] = ci;
    corners[ci++] = topOuter(i);
    corners[ci++] = topOuter(i1);
    corners[ci++] = topInner(i1);
    corners[ci++] = topInner(i);

    // Bottom cap ring, wound CCW from -Z.
    offsets[fi++] = ci;
    corners[ci++] = botInner(i);
    corners[ci++] = botInner(i1);
    corners[ci++] = botOuter(i1);
    corners[ci++] = botOuter(i);

    // Outer wall, normals outward from the solid material.
    offsets[fi++] = ci;
    corners[ci++] = botOuter(i);
    corners[ci++] = botOuter(i1);
    corners[ci++] = topOuter(i1);
    corners[ci++] = topOuter(i);

    // Inner wall, normals into the hollow void.
    offsets[fi++] = ci;
    corners[ci++] = topInner(i);
    corners[ci++] = topInner(i1);
    corners[ci++] = botInner(i1);
    corners[ci++] = botInner(i);
  }
  offsets[fi] = ci;

  const topology = {
    offsets,
    corners,
    ...buildEdgesAndCornerEdges(offsets, corners),
  };

  const outerU = loopUCoordinates(outerLoop);
  const innerU = loopUCoordinates(innerLoop);
  const uvs = new Float32Array(cornerCount * 2);
  let ui = 0;
  for (let i = 0; i < n; i++) {
    const i1 = i + 1;
    const ou0 = outerU[i];
    const ou1 = outerU[i1];
    const iu0 = innerU[i];
    const iu1 = innerU[i1];

    // Top cap: outer edge at v=0, inner edge at v=1.
    uvs[ui++] = ou0;
    uvs[ui++] = 0;
    uvs[ui++] = ou1;
    uvs[ui++] = 0;
    uvs[ui++] = ou1;
    uvs[ui++] = 1;
    uvs[ui++] = ou0;
    uvs[ui++] = 1;

    // Bottom cap.
    uvs[ui++] = ou0;
    uvs[ui++] = 1;
    uvs[ui++] = ou1;
    uvs[ui++] = 1;
    uvs[ui++] = ou1;
    uvs[ui++] = 0;
    uvs[ui++] = ou0;
    uvs[ui++] = 0;

    // Outer wall.
    uvs[ui++] = ou0;
    uvs[ui++] = 0;
    uvs[ui++] = ou1;
    uvs[ui++] = 0;
    uvs[ui++] = ou1;
    uvs[ui++] = 1;
    uvs[ui++] = ou0;
    uvs[ui++] = 1;

    // Inner wall.
    uvs[ui++] = iu0;
    uvs[ui++] = 1;
    uvs[ui++] = iu1;
    uvs[ui++] = 1;
    uvs[ui++] = iu1;
    uvs[ui++] = 0;
    uvs[ui++] = iu0;
    uvs[ui++] = 0;
  }

  return {
    vertices: verts,
    indices: triangulateTopologyForRendering(topology),
    loopNormals: recomputeLoopNormals(verts, topology),
    topology,
    uvs,
  };
}

function loopUCoordinates(loop: ReadonlyArray<Vec2>): number[] {
  const n = loop.length;
  const edgeLengths = new Array<number>(n);
  let perimeter = 0;
  for (let i = 0; i < n; i++) {
    const [x0, y0] = loop[i];
    const [x1, y1] = loop[(i + 1) % n];
    const length = Math.hypot(x1 - x0, y1 - y0);
    edgeLengths[i] = length;
    perimeter += length;
  }

  const u = new Array<number>(n + 1);
  if (perimeter <= 0) {
    for (let i = 0; i <= n; i++) u[i] = i / n;
    return u;
  }

  let distance = 0;
  for (let i = 0; i < n; i++) {
    u[i] = distance / perimeter;
    distance += edgeLengths[i];
  }
  u[n] = 1;
  return u;
}

// ---------------------------------------------------------------------------
// Profiled prism builders
// ---------------------------------------------------------------------------

interface ProfiledPrismResult {
  geometry: PrimitiveGeometry;
  endCapZ: number;
}

function buildProfiledPrism(
  outerLoop: ReadonlyArray<Vec2>,
  holes: readonly ReadonlyArray<Vec2>[],
  height: number,
  cutStartOffset: number,
  reverse: boolean,
  anchor: ZAnchor,
  profile: Extract<SurfaceCutterProfile, { kind: "linearRamp" }>,
): ProfiledPrismResult | null {
  const totalDepth = height + cutStartOffset;
  const geometry = buildExtrudedPolygon(
    outerLoop,
    Math.max(1e-4, totalDepth),
    holes,
  );
  const vertexCount = geometry.vertices.length / 3;
  if (!Number.isInteger(vertexCount) || vertexCount < 6) return null;
  const layerSize = vertexCount / 2;
  if (!Number.isInteger(layerSize)) return null;

  const baseZ = reverse ? cutStartOffset : 0;
  const zOffset =
    anchor === "centered"
      ? reverse
        ? height / 2 - cutStartOffset / 2
        : -height / 2
      : 0;

  for (let vi = 0; vi < layerSize; vi++) {
    const top = vi * 3;
    const bottom = (layerSize + vi) * 3;
    const x = geometry.vertices[top]!;
    const y = geometry.vertices[top + 1]!;
    const rampZ = surfaceProfileRampZ(
      profile,
      x,
      y,
      height,
      reverse,
      cutStartOffset,
    );

    if (reverse) {
      geometry.vertices[top + 2] = baseZ + zOffset;
      geometry.vertices[bottom + 2] = rampZ + zOffset;
    } else {
      geometry.vertices[top + 2] = rampZ + zOffset;
      geometry.vertices[bottom + 2] = baseZ + zOffset;
    }
  }

  return {
    geometry: {
      ...geometry,
      loopNormals: recomputeLoopNormals(geometry.vertices, geometry.topology),
    },
    endCapZ:
      anchor === "centered"
        ? reverse
          ? -(height + cutStartOffset) / 2
          : height / 2
        : reverse
          ? -height
          : height,
  };
}

// ---------------------------------------------------------------------------
// Layered prism builders — cap scale, loop cuts, and taper profile
// ---------------------------------------------------------------------------

interface SurfaceLoopOptions {
  capScale: number;
  loopCuts: number;
  loopProfile: number;
}

interface LayeredPrismResult {
  geometry: PrimitiveGeometry;
  endCapZ: number;
}

interface LayeredLoopData {
  loops: THREE.Vector2[][];
  loopStarts: readonly number[];
  allPoints: readonly THREE.Vector2[];
  triangles: readonly number[][];
  center: readonly [number, number];
}

type LayeredCapTopology = "structured" | "triangulated";

interface StructuredLayeredCapPlan {
  outer: readonly number[];
  inner: readonly number[] | null;
}

function buildLayeredPrism(
  outerLoop: ReadonlyArray<Vec2>,
  holes: readonly ReadonlyArray<Vec2>[],
  height: number,
  cutStartOffset: number,
  reverse: boolean,
  anchor: ZAnchor,
  profile: SurfaceCutterProfile,
  options: SurfaceLoopOptions,
  capTopology: LayeredCapTopology,
): LayeredPrismResult | null {
  const loopData = prepareLayeredLoops(outerLoop, holes);
  if (!loopData) return null;

  const ringCount = Math.max(2, options.loopCuts + 2);
  const pointCount = loopData.allPoints.length;
  const verts = new Float32Array(ringCount * pointCount * 3);
  const zOffset =
    anchor === "centered"
      ? reverse
        ? height / 2 - cutStartOffset / 2
        : -height / 2
      : 0;
  const baseZ = (reverse ? cutStartOffset : 0) + zOffset;
  const normalizedProfile = normalizeSurfaceProfile(profile);
  const [cx, cy] = loopData.center;

  for (let ring = 0; ring < ringCount; ring++) {
    const t = ring / (ringCount - 1);
    const shaped = surfaceLoopProfileT(t, options.loopProfile);
    const xyScale = 1 + (options.capScale - 1) * shaped;

    for (let pi = 0; pi < pointCount; pi++) {
      const point = loopData.allPoints[pi]!;
      const x = cx + (point.x - cx) * xyScale;
      const y = cy + (point.y - cy) * xyScale;
      const capZ =
        normalizedProfile.kind === "linearRamp"
          ? surfaceProfileRampZ(
              normalizedProfile,
              x,
              y,
              height,
              reverse,
              cutStartOffset,
            ) + zOffset
          : (reverse ? -height : height) + zOffset;
      const vi = (ring * pointCount + pi) * 3;
      verts[vi] = x;
      verts[vi + 1] = y;
      verts[vi + 2] = baseZ + (capZ - baseZ) * t;
    }
  }

  const structuredCap =
    capTopology === "structured" ? structuredLayeredCapPlan(loopData) : null;
  const nTris = loopData.triangles.length;
  const nBands = ringCount - 1;
  const nWallQuads = nBands * pointCount;
  const capFacesPerEnd = structuredCap
    ? structuredCap.inner
      ? structuredCap.outer.length
      : 1
    : nTris;
  const capCornersPerEnd = structuredCap
    ? structuredCap.inner
      ? structuredCap.outer.length * 4
      : structuredCap.outer.length
    : nTris * 3;
  const nFaces = 2 * capFacesPerEnd + nWallQuads;
  const nCorners = 2 * capCornersPerEnd + nWallQuads * 4;
  const offsets = new Uint32Array(nFaces + 1);
  const corners = new Uint32Array(nCorners);
  const uvs = new Float32Array(nCorners * 2);
  const uvBounds = planarUvBounds(loopData.allPoints);
  let ci = 0;
  let fi = 0;
  let ui = 0;

  const idx = (ring: number, point: number): number =>
    ring * pointCount + point;
  const pushCorner = (vertexIndex: number): void => {
    corners[ci++] = vertexIndex;
    const base = vertexIndex * 3;
    uvs[ui++] = (verts[base]! - uvBounds.minX) / uvBounds.rangeX;
    uvs[ui++] = (verts[base + 1]! - uvBounds.minY) / uvBounds.rangeY;
  };
  const pushWallCorner = (vertexIndex: number, u: number, v: number): void => {
    corners[ci++] = vertexIndex;
    uvs[ui++] = u;
    uvs[ui++] = v;
  };
  const pushFace = (vertexIndices: readonly number[]): void => {
    offsets[fi++] = ci;
    for (const vertexIndex of vertexIndices) pushCorner(vertexIndex);
  };
  const pushStructuredCap = (
    ring: number,
    normalSign: 1 | -1,
    plan: StructuredLayeredCapPlan,
  ): void => {
    const inner = plan.inner;
    if (!inner) {
      const face = plan.outer.map((point) => idx(ring, point));
      pushFace(normalSign > 0 ? face : face.reverse());
      return;
    }

    for (let i = 0; i < plan.outer.length; i++) {
      const i1 = (i + 1) % plan.outer.length;
      const face = [
        idx(ring, plan.outer[i]!),
        idx(ring, plan.outer[i1]!),
        idx(ring, inner[i1]!),
        idx(ring, inner[i]!),
      ];
      pushFace(normalSign > 0 ? face : face.reverse());
    }
  };
  const pushTriangulatedCap = (ring: number, normalSign: 1 | -1): void => {
    for (const [a, b, c] of loopData.triangles) {
      pushFace(
        normalSign > 0
          ? [idx(ring, a!), idx(ring, b!), idx(ring, c!)]
          : [idx(ring, a!), idx(ring, c!), idx(ring, b!)],
      );
    }
  };
  const pushCap = (ring: number, normalSign: 1 | -1): void => {
    if (structuredCap) {
      pushStructuredCap(ring, normalSign, structuredCap);
      return;
    }
    pushTriangulatedCap(ring, normalSign);
  };

  const baseRing = 0;
  const capRing = ringCount - 1;
  if (reverse) {
    pushCap(baseRing, 1);
    pushCap(capRing, -1);
  } else {
    pushCap(capRing, 1);
    pushCap(baseRing, -1);
  }

  for (let li = 0; li < loopData.loops.length; li++) {
    const loop = loopData.loops[li]!;
    const loopStart = loopData.loopStarts[li]!;
    const uCoords = loopUCoordinates(vectorLoopToVec2(loop));
    for (let ring = 0; ring < nBands; ring++) {
      const v0 = ring / nBands;
      const v1 = (ring + 1) / nBands;
      for (let i = 0; i < loop.length; i++) {
        const i1 = (i + 1) % loop.length;
        const u0 = uCoords[i]!;
        const u1 = uCoords[i + 1]!;
        offsets[fi++] = ci;
        if (reverse) {
          pushWallCorner(idx(ring, loopStart + i), u0, v0);
          pushWallCorner(idx(ring + 1, loopStart + i), u0, v1);
          pushWallCorner(idx(ring + 1, loopStart + i1), u1, v1);
          pushWallCorner(idx(ring, loopStart + i1), u1, v0);
        } else {
          pushWallCorner(idx(ring + 1, loopStart + i), u0, v1);
          pushWallCorner(idx(ring, loopStart + i), u0, v0);
          pushWallCorner(idx(ring, loopStart + i1), u1, v0);
          pushWallCorner(idx(ring + 1, loopStart + i1), u1, v1);
        }
      }
    }
  }
  offsets[fi] = ci;

  const topology = {
    offsets,
    corners,
    ...buildEdgesAndCornerEdges(offsets, corners),
  };

  return {
    geometry: {
      vertices: verts,
      indices: triangulateTopologyForRendering(topology),
      loopNormals: recomputeLoopNormals(verts, topology),
      topology,
      uvs,
    },
    endCapZ:
      anchor === "centered"
        ? reverse
          ? -(height + cutStartOffset) / 2
          : height / 2
        : reverse
          ? -height
          : height,
  };
}

function prepareLayeredLoops(
  outerLoop: ReadonlyArray<Vec2>,
  holes: readonly ReadonlyArray<Vec2>[],
): LayeredLoopData | null {
  if (outerLoop.length < 3) return null;

  const outline = vectorLoop(outerLoop);
  if (THREE.ShapeUtils.area(outline) < 0) outline.reverse();

  const holeLoops: THREE.Vector2[][] = [];
  for (const hole of holes) {
    if (hole.length < 3) continue;
    const loop = vectorLoop(hole);
    if (THREE.ShapeUtils.area(loop) > 0) loop.reverse();
    holeLoops.push(loop);
  }

  const triangles = THREE.ShapeUtils.triangulateShape(outline, holeLoops);
  if (triangles.length === 0) return null;

  const loops = [outline, ...holeLoops];
  const loopStarts: number[] = [];
  const allPoints: THREE.Vector2[] = [];
  for (const loop of loops) {
    loopStarts.push(allPoints.length);
    allPoints.push(...loop);
  }

  let cx = 0;
  let cy = 0;
  for (const point of outline) {
    cx += point.x;
    cy += point.y;
  }
  cx /= outline.length;
  cy /= outline.length;

  return {
    loops,
    loopStarts,
    allPoints,
    triangles,
    center: [cx, cy],
  };
}

function structuredLayeredCapPlan(
  loopData: LayeredLoopData,
): StructuredLayeredCapPlan | null {
  const outer = loopData.loops[0];
  if (!outer || outer.length < 3) return null;
  const outerIndices = outer.map((_, index) => index);
  if (loopData.loops.length === 1) {
    return { outer: outerIndices, inner: null };
  }

  if (loopData.loops.length !== 2) return null;
  const inner = loopData.loops[1];
  const innerStart = loopData.loopStarts[1];
  if (!inner || innerStart === undefined || inner.length !== outer.length) {
    return null;
  }

  const innerIndices = inner.map(
    (_, index) => innerStart + inner.length - 1 - index,
  );
  return { outer: outerIndices, inner: innerIndices };
}

function surfaceLoopProfileT(t: number, profile: number): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  const p = Number.isFinite(profile) ? Math.max(0, Math.min(1, profile)) : 0.5;
  if (Math.abs(p - 0.5) < 1e-6) return t;

  const exponent = 4 ** ((p - 0.5) * 2);
  const x = t;
  const yBase = 1 - t ** exponent;
  const y = yBase <= 0 ? 0 : yBase ** (1 / exponent);
  return Math.max(0, Math.min(1, (x - y + 1) * 0.5));
}

function vectorLoop(loop: ReadonlyArray<Vec2>): THREE.Vector2[] {
  return loop.map(([x, y]) => new THREE.Vector2(x, y));
}

function vectorLoopToVec2(loop: readonly THREE.Vector2[]): Vec2[] {
  return loop.map((point) => [point.x, point.y]);
}

function planarUvBounds(points: readonly THREE.Vector2[]): {
  minX: number;
  minY: number;
  rangeX: number;
  rangeY: number;
} {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const point of points) {
    minX = Math.min(minX, point.x);
    maxX = Math.max(maxX, point.x);
    minY = Math.min(minY, point.y);
    maxY = Math.max(maxY, point.y);
  }
  return {
    minX,
    minY,
    rangeX: Math.max(1e-9, maxX - minX),
    rangeY: Math.max(1e-9, maxY - minY),
  };
}

// ---------------------------------------------------------------------------
// Solid builders — preserve current confirm-time behavior bit-for-bit
// ---------------------------------------------------------------------------

interface SolidResult {
  geometry: PrimitiveGeometry;
  scale: [number, number, number];
  endCapZ: number;
  generator: SceneObject["generator"] | undefined;
}

function buildSolidBox(
  spec: Extract<SurfaceCutterSpec, { kind: "box" }>,
): SolidResult {
  const totalHeight = spec.height + spec.cutStartOffset;
  const generatorParams = { size: 1, subdivisions: 0 };
  return {
    geometry: generateCube(generatorParams),
    scale: [spec.width, spec.depth, totalHeight],
    endCapZ: spec.reverse ? -0.5 : 0.5,
    generator: {
      generatorId: "cube",
      params: generatorParams,
      worldSpace: false,
    },
  };
}

function buildSolidCylinder(
  spec: Extract<SurfaceCutterSpec, { kind: "cylinder" }>,
): SolidResult {
  const totalHeight = spec.height + spec.cutStartOffset;
  const generatorParams = {
    radius: spec.radiusX,
    height: totalHeight,
    segments: spec.segments,
  };
  // Oval support via Y scale over a circular cylinder — preserves editable
  // radius/segments and lets future bake/apply operations stay clean.
  const yScale = spec.radiusX > 0 ? spec.radiusY / spec.radiusX : 1;
  return {
    geometry: generateCylinder(generatorParams),
    scale: [1, yScale, 1],
    endCapZ: spec.reverse ? -totalHeight / 2 : totalHeight / 2,
    generator: {
      generatorId: "cylinder",
      params: generatorParams,
      worldSpace: false,
    },
  };
}

function buildSolidPoly(
  spec: Extract<SurfaceCutterSpec, { kind: "poly" }>,
): SolidResult {
  // spec.height is the cut depth magnitude; reverse direction comes from
  // spec.reverse and is encoded in vertex Z (not via reflected basis).
  const totalHeight = spec.height + spec.cutStartOffset;
  const geo = buildExtrudedPolygon(spec.outerLoop, totalHeight);
  if (spec.reverse) {
    for (let i = 2; i < geo.vertices.length; i += 3) {
      geo.vertices[i] -= spec.height;
    }
  }
  return {
    geometry: geo,
    scale: [1, 1, 1],
    endCapZ: spec.reverse ? -spec.height : spec.height,
    generator: undefined,
  };
}

// ---------------------------------------------------------------------------
// Dispatcher
// ---------------------------------------------------------------------------

/**
 * Build the cutter mesh for a surface operator at a given parameter set.
 *
 * Passing `hollow: false` emits the solid cutter; passing `hollow: true`
 * swaps the geometry for a thick-walled shell. The dispatcher also returns
 * the historical solid-mode scale/generator metadata so callers can either
 * preserve primitive semantics or bake dimensions into persistent surface
 * generator geometry.
 */
export function buildSurfaceCutterMesh(
  spec: SurfaceCutterSpec,
  options: SurfaceCutterOptions,
): SurfaceCutterMesh {
  const wantHollow = options.hollow && options.hollowThickness > 0;
  const profile = normalizeSurfaceProfile(spec.profile);
  const loopOptions = normalizeSurfaceLoopOptions(options);

  if (requiresLayeredPrism(loopOptions)) {
    const layered = tryBuildLayered(
      spec,
      profile,
      wantHollow ? options.hollowThickness : 0,
      loopOptions,
    );
    if (layered) {
      return {
        geometry: layered.geometry,
        scale: [1, 1, 1],
        endCapZ: layered.endCapZ,
        generator: undefined,
        hollowFallback: layered.hollowFallback,
      };
    }
  }

  if (profile.kind === "linearRamp") {
    const profiled = tryBuildProfiled(
      spec,
      profile,
      wantHollow ? options.hollowThickness : 0,
    );
    if (profiled && (!wantHollow || !profiled.hollowFallback)) {
      return {
        geometry: profiled.geometry,
        scale: [1, 1, 1],
        endCapZ: profiled.endCapZ,
        generator: undefined,
        hollowFallback: profiled.hollowFallback,
      };
    }

    if (profiled) {
      return {
        geometry: profiled.geometry,
        scale: [1, 1, 1],
        endCapZ: profiled.endCapZ,
        generator: undefined,
        hollowFallback: true,
      };
    }
  }

  if (!wantHollow) {
    const solid = solidFor(spec);
    return {
      geometry: solid.geometry,
      scale: solid.scale,
      endCapZ: solid.endCapZ,
      generator: solid.generator,
      hollowFallback: false,
    };
  }

  const hollow = tryBuildHollow(spec, options.hollowThickness);
  if (hollow) {
    return {
      geometry: hollow.geometry,
      scale: [1, 1, 1],
      endCapZ: hollow.endCapZ,
      generator: undefined,
      hollowFallback: false,
    };
  }

  // Hollow requested but couldn't be built (concave self-intersection,
  // thickness too large, or degenerate input). Fall back to solid so the
  // user still gets a sensible result, and flag it.
  const solid = solidFor(spec);
  return {
    geometry: solid.geometry,
    scale: solid.scale,
    endCapZ: solid.endCapZ,
    generator: solid.generator,
    hollowFallback: true,
  };
}

function normalizeSurfaceLoopOptions(
  options: SurfaceCutterOptions,
): SurfaceLoopOptions {
  const capScale =
    typeof options.capScale === "number" && Number.isFinite(options.capScale)
      ? Math.max(0, options.capScale)
      : 1;
  const loopCuts =
    typeof options.loopCuts === "number" && Number.isFinite(options.loopCuts)
      ? Math.max(0, Math.min(64, Math.round(options.loopCuts)))
      : 0;
  const loopProfile =
    typeof options.loopProfile === "number" &&
    Number.isFinite(options.loopProfile)
      ? Math.max(0, Math.min(1, options.loopProfile))
      : 0.5;
  return { capScale, loopCuts, loopProfile };
}

function requiresLayeredPrism(options: SurfaceLoopOptions): boolean {
  return options.loopCuts > 0 || Math.abs(options.capScale - 1) > 1e-8;
}

function solidFor(spec: SurfaceCutterSpec): SolidResult {
  switch (spec.kind) {
    case "box":
      return buildSolidBox(spec);
    case "cylinder":
      return buildSolidCylinder(spec);
    case "poly":
      return buildSolidPoly(spec);
  }
}

function surfaceCutterOuterLoop(spec: SurfaceCutterSpec): ReadonlyArray<Vec2> {
  switch (spec.kind) {
    case "box":
      return sampleSurfaceRectangleLoop(spec.width, spec.depth);
    case "cylinder":
      return sampleSurfaceEllipseLoop(
        spec.radiusX,
        spec.radiusY,
        spec.segments,
      );
    case "poly":
      return spec.outerLoop;
  }
}

function tryBuildLayered(
  spec: SurfaceCutterSpec,
  profile: SurfaceCutterProfile,
  rawThickness: number,
  options: SurfaceLoopOptions,
): (LayeredPrismResult & { hollowFallback: boolean }) | null {
  const outer = surfaceCutterOuterLoop(spec);
  if (outer.length < 3) return null;
  const anchor: ZAnchor = spec.kind === "poly" ? "anchored" : "centered";
  const capTopology: LayeredCapTopology =
    spec.kind === "poly" ? "triangulated" : "structured";

  if (rawThickness > 0) {
    const thickness = clampSurfaceHollowThickness(
      rawThickness,
      surfaceCutterFootprintMin(spec),
    );
    if (thickness > 0) {
      const inset = insetPolygon2D(outer, thickness);
      if (inset.valid && inset.loop.length >= 3) {
        const hollow = buildLayeredPrism(
          outer,
          [inset.loop],
          spec.height,
          spec.cutStartOffset,
          spec.reverse,
          anchor,
          profile,
          options,
          capTopology,
        );
        if (hollow) return { ...hollow, hollowFallback: false };
      }
    }

    const solidFallback = buildLayeredPrism(
      outer,
      [],
      spec.height,
      spec.cutStartOffset,
      spec.reverse,
      anchor,
      profile,
      options,
      capTopology,
    );
    return solidFallback ? { ...solidFallback, hollowFallback: true } : null;
  }

  const solid = buildLayeredPrism(
    outer,
    [],
    spec.height,
    spec.cutStartOffset,
    spec.reverse,
    anchor,
    profile,
    options,
    capTopology,
  );
  return solid ? { ...solid, hollowFallback: false } : null;
}

function tryBuildProfiled(
  spec: SurfaceCutterSpec,
  profile: Extract<SurfaceCutterProfile, { kind: "linearRamp" }>,
  rawThickness: number,
): (ProfiledPrismResult & { hollowFallback: boolean }) | null {
  const outer = surfaceCutterOuterLoop(spec);
  if (outer.length < 3) return null;
  const anchor: ZAnchor = spec.kind === "poly" ? "anchored" : "centered";

  if (rawThickness > 0) {
    const thickness = clampSurfaceHollowThickness(
      rawThickness,
      surfaceCutterFootprintMin(spec),
    );
    if (thickness > 0) {
      const inset = insetPolygon2D(outer, thickness);
      if (inset.valid && inset.loop.length >= 3) {
        const hollow = buildProfiledPrism(
          outer,
          [inset.loop],
          spec.height,
          spec.cutStartOffset,
          spec.reverse,
          anchor,
          profile,
        );
        if (hollow) return { ...hollow, hollowFallback: false };
      }
    }
    const solidFallback = buildProfiledPrism(
      outer,
      [],
      spec.height,
      spec.cutStartOffset,
      spec.reverse,
      anchor,
      profile,
    );
    return solidFallback ? { ...solidFallback, hollowFallback: true } : null;
  }

  const solid = buildProfiledPrism(
    outer,
    [],
    spec.height,
    spec.cutStartOffset,
    spec.reverse,
    anchor,
    profile,
  );
  return solid ? { ...solid, hollowFallback: false } : null;
}

function tryBuildHollow(
  spec: SurfaceCutterSpec,
  rawThickness: number,
): HollowPrismResult | null {
  const footprintMin = surfaceCutterFootprintMin(spec);
  const thickness = clampSurfaceHollowThickness(rawThickness, footprintMin);
  if (thickness <= 0) return null;

  switch (spec.kind) {
    case "box": {
      const outer = sampleSurfaceRectangleLoop(spec.width, spec.depth);
      return buildHollowPrism(
        outer,
        thickness,
        spec.height,
        spec.cutStartOffset,
        "centered",
        spec.reverse,
        "quadBands",
      );
    }
    case "cylinder": {
      const outer = sampleSurfaceEllipseLoop(
        spec.radiusX,
        spec.radiusY,
        spec.segments,
      );
      return buildHollowPrism(
        outer,
        thickness,
        spec.height,
        spec.cutStartOffset,
        "centered",
        spec.reverse,
        "quadBands",
      );
    }
    case "poly": {
      return buildHollowPrism(
        spec.outerLoop,
        thickness,
        spec.height,
        spec.cutStartOffset,
        "anchored",
        spec.reverse,
        "triangulated",
      );
    }
  }
}

/**
 * Minimum footprint extent for the cutter spec — the geometric ceiling for
 * hollow thickness (since insetting by more than half the smallest dimension
 * collapses the inner loop).
 *
 * For poly, the bounding-box minor axis is a robust upper bound; the exact
 * "max safe inset" needs straight-skeleton math, but the inset validator
 * catches tighter self-intersection cases beneath this ceiling.
 */
export function surfaceCutterFootprintMin(spec: SurfaceCutterSpec): number {
  switch (spec.kind) {
    case "box":
      return Math.min(spec.width, spec.depth);
    case "cylinder":
      return 2 * Math.min(spec.radiusX, spec.radiusY);
    case "poly":
      return polyMinorExtent(spec.outerLoop);
  }
}

function polyMinorExtent(loop: ReadonlyArray<Vec2>): number {
  if (loop.length < 3) return 0;
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const [x, y] of loop) {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  return Math.min(maxX - minX, maxY - minY);
}
